import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePrintOrderRun } from '../../hooks/usePrintOrderRun';
import { useReprintByPrefix } from '../../hooks/useReprintByPrefix';
import ProductInfoModal from '../shared/ProductInfoModal';
import { cancelPrintOrderAll, cancelPrintOrderMissing } from '../../services/printOrdersAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { PrintOrderWithSheetsResponse } from '../../schemas/apiResponse.schemas';
import type { PrintOrderSource, PrintOrderUnitInput } from '@/shared/types/printOrder.types';
import type { DestinationPrinterId } from '@/shared/types/printer.types';
import {
  PRINT_EVENT_NO_LABELS_CONFIRM,
  PRINT_EVENT_NO_MISSING_TO_DELETE,
  PRINT_EVENT_NO_MISSING_TO_REPRINT,
  aggregatePrintJobIds,
  flattenPrintOrderItems,
  idsAfter,
  isPrintEventHandlingKind,
  printJobIdRowTone,
  type PrintJobIdRow,
} from '@/shared/utils/printJobIds';
import {
  currentPrintEventReturnPath,
  printEventScanLabelPath,
} from '../../utils/scanLabelHandoff';
import '../orders/OrdersPilot.css';
import './PrintBoxLabel.css';

interface PrintEventHandlingModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly job: PrintOrderWithSheetsResponse;
  readonly source: PrintOrderSource;
  readonly printerId: DestinationPrinterId;
  readonly initialScannedPrefix?: string | null;
}

function unitsForMissing(missing: readonly PrintJobIdRow[]): {
  readonly units: PrintOrderUnitInput[];
  readonly copiesPerId: number;
} {
  const counts = new Set(missing.map((row) => row.count));
  if (counts.size <= 1) {
    return {
      copiesPerId: missing[0]?.count ?? 1,
      units: missing.map((row) => ({
        sku: row.sku,
        shelfIndex: row.shelfIndex,
        baseQty: row.baseQty,
        storageId: row.storageId,
      })),
    };
  }

  return {
    copiesPerId: 1,
    units: missing.flatMap((row) =>
      Array.from({ length: row.count }, () => ({
        sku: row.sku,
        shelfIndex: row.shelfIndex,
        baseQty: row.baseQty,
        storageId: row.storageId,
      })),
    ),
  };
}

function PrintEventHandlingModal({
  isOpen,
  onClose,
  job,
  source,
  printerId,
  initialScannedPrefix = null,
}: PrintEventHandlingModalProps): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { runPrintOrder, isRunning: reprintMissingRunning } = usePrintOrderRun();
  const { reprint, isRunning: reprintOneRunning } = useReprintByPrefix(source);
  const idRows = useMemo(
    () => aggregatePrintJobIds(flattenPrintOrderItems(job.sheets)),
    [job.sheets],
  );

  const [scannedPrefix, setScannedPrefix] = useState<string | null>(initialScannedPrefix);
  const [actionMessage, setActionMessage] = useState('');
  const [actionIsError, setActionIsError] = useState(false);
  const [showSuccessFlash, setShowSuccessFlash] = useState(false);
  const [cancelRunning, setCancelRunning] = useState(false);
  const [productInfoSku, setProductInfoSku] = useState<string | null>(null);

  const actionRunning = reprintMissingRunning || reprintOneRunning || cancelRunning;

  useEffect(() => {
    if (!initialScannedPrefix) return undefined;
    setScannedPrefix(initialScannedPrefix);
    setShowSuccessFlash(true);
    const timer = window.setTimeout(() => setShowSuccessFlash(false), 1000);
    return () => window.clearTimeout(timer);
  }, [initialScannedPrefix]);

  const handleReprintLabel = useCallback(async (): Promise<void> => {
    if (!scannedPrefix || actionRunning) return;
    setActionMessage('');
    const result = await reprint({
      storageIdPrefix: scannedPrefix,
      isBigLabel: job.order.labelKind === 'big',
      copies: 1,
      printerId,
    });
    setActionIsError(!result.completed);
    setActionMessage(result.message);
  }, [actionRunning, job.order.labelKind, printerId, reprint, scannedPrefix]);

  const handleReprintMissing = useCallback(async (): Promise<void> => {
    if (!scannedPrefix || actionRunning) return;
    const missing = idsAfter(idRows, scannedPrefix);
    if (missing.length === 0) {
      setActionIsError(true);
      setActionMessage(PRINT_EVENT_NO_MISSING_TO_REPRINT);
      return;
    }

    const { units, copiesPerId } = unitsForMissing(missing);
    setActionMessage('');
    const result = await runPrintOrder(
      {
        labelKind: job.order.labelKind,
        labelType: job.order.labelType,
        source,
        copiesPerId,
        units,
      },
      { printerId },
    );
    setActionIsError(!result.completed);
    setActionMessage(result.message);
  }, [
    actionRunning,
    idRows,
    job.order.labelKind,
    job.order.labelType,
    printerId,
    runPrintOrder,
    scannedPrefix,
    source,
  ]);

  const handleCancelMissing = useCallback(async (): Promise<void> => {
    if (!scannedPrefix || actionRunning) return;
    if (idsAfter(idRows, scannedPrefix).length === 0) {
      setActionIsError(true);
      setActionMessage(PRINT_EVENT_NO_MISSING_TO_DELETE);
      return;
    }

    setCancelRunning(true);
    setActionMessage('');
    try {
      await cancelPrintOrderMissing(job.order.id, scannedPrefix);
      setActionIsError(false);
      setActionMessage('Cancelled missing labels.');
    } catch (error: unknown) {
      setActionIsError(true);
      setActionMessage(getApiErrorMessage(error, 'Failed to cancel missing labels.'));
    } finally {
      setCancelRunning(false);
    }
  }, [actionRunning, idRows, job.order.id, scannedPrefix]);

  const handleNoLabelsPrinted = useCallback(async (): Promise<void> => {
    if (actionRunning || idRows.length === 0) return;
    if (!window.confirm(PRINT_EVENT_NO_LABELS_CONFIRM)) return;
    setCancelRunning(true);
    setActionMessage('');
    try {
      await cancelPrintOrderAll(job.order.id);
      const { units, copiesPerId } = unitsForMissing(idRows);
      const result = await runPrintOrder(
        {
          labelKind: job.order.labelKind,
          labelType: job.order.labelType,
          source,
          copiesPerId,
          units,
        },
        { printerId },
      );
      setActionIsError(!result.completed);
      setActionMessage(result.message);
    } catch (error: unknown) {
      setActionIsError(true);
      setActionMessage(getApiErrorMessage(error, 'Failed to delete print-order IDs.'));
    } finally {
      setCancelRunning(false);
    }
  }, [
    actionRunning,
    idRows,
    job.order.id,
    job.order.labelKind,
    job.order.labelType,
    printerId,
    runPrintOrder,
    source,
  ]);

  if (!isOpen) return null;

  return (
    <div className="pbl-modal-overlay peh-overlay" onClick={onClose}>
      <div className="pbl-modal peh-modal" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 className="pbl-modal-title">Print Event Handling</h2>

        <table className="peh-table">
          <thead>
            <tr>
              <th scope="col">Order</th>
              <th scope="col">SKU</th>
              <th scope="col">ID</th>
              <th scope="col">Count</th>
            </tr>
          </thead>
          <tbody>
            {idRows.map((row, index) => {
              const tone = scannedPrefix ? printJobIdRowTone(idRows, scannedPrefix, row.prefix) : null;
              const toneClass = tone ? ` peh-row-${tone}` : '';
              return (
                <tr key={row.storageId} className={toneClass.trim() || undefined}>
                  <td>{index + 1}</td>
                  <td className="peh-sku">
                    {row.sku ? (
                      <button
                        type="button"
                        className="peh-sku-button"
                        onClick={() => setProductInfoSku(row.sku)}
                      >
                        {row.sku}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{row.prefix}</td>
                  <td>{row.count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {scannedPrefix ? (
          <div className="peh-scanned">
            <span className="pbl-info-tag pbl-storage-id-tag">{scannedPrefix}</span>
          </div>
        ) : null}

        {actionMessage ? (
          <div className={`pbl-message${actionIsError ? ' pbl-message-error' : ''}`} role="status">
            {actionMessage}
          </div>
        ) : null}

        <div className="peh-actions">
          {scannedPrefix || idRows.length === 0 ? null : (
            <>
              <button
                type="button"
                className="home-button"
                onClick={() => {
                  navigate(printEventScanLabelPath(
                    job.order.id,
                    currentPrintEventReturnPath(location.pathname, location.search),
                  ));
                }}
                disabled={actionRunning}
              >
                Scan Last Label
              </button>
              {isPrintEventHandlingKind(job.order.labelKind) ? (
                <button
                  type="button"
                  className="home-button"
                  onClick={() => {
                    void handleNoLabelsPrinted();
                  }}
                  disabled={actionRunning}
                >
                  No Labels Printed :(
                </button>
              ) : null}
            </>
          )}
          {scannedPrefix ? (
            <>
              <button
                type="button"
                className="home-button"
                onClick={() => {
                  void handleReprintLabel();
                }}
                disabled={actionRunning}
              >
                Reprint label
              </button>
              <button
                type="button"
                className="home-button"
                onClick={() => {
                  void handleReprintMissing();
                }}
                disabled={actionRunning}
              >
                Reprint missing labels
              </button>
              <button
                type="button"
                className="home-button"
                onClick={() => {
                  void handleCancelMissing();
                }}
                disabled={actionRunning}
              >
                Cancel missing labels
              </button>
            </>
          ) : null}
        </div>
      </div>

      {productInfoSku ? (
        <div className="peh-nested-modal" onClick={(event) => event.stopPropagation()}>
          <ProductInfoModal item={{ sku: productInfoSku }} onClose={() => setProductInfoSku(null)} />
        </div>
      ) : null}

      {showSuccessFlash ? (
        <div className="success-flash peh-success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      ) : null}
    </div>
  );
}

export default PrintEventHandlingModal;
